/**
 * Reaching a workspace file's bytes from the chat: a signed URL fetched once it is
 * needed, and a download.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '@/services/api';
import { fileName } from './fileRefs';

export function downloadWorkspaceFile(threadId: string, path: string): void {
  api.get<{ url: string }>(`/chat/threads/${threadId}/workspace/download`, { path })
    .then(({ url }) => {
      const a = document.createElement('a');
      a.href = url; a.download = fileName(path); a.target = '_blank'; a.click();
    })
    .catch((err) => console.warn('[Workspace] Download failed:', err));
}

// ── A file's URL, for showing it in the chat ────────────────────────────────

/**
 * A signed URL for a file, fetched once its element is near the screen: a long
 * thread should not sign every picture it ever showed. Signed URLs last an hour,
 * so `onError` (a tab left open, then scrolled back to) asks for a new one, once;
 * after that the file is really gone and `failed` says so.
 */
export function useFileUrl(threadId: string | null, path: string) {
  const ref = useRef<HTMLElement | null>(null);
  const [near, setNear] = useState(false);
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el || near) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) setNear(true);
    }, { rootMargin: '400px' });
    observer.observe(el);
    return () => observer.disconnect();
  }, [near]);

  useEffect(() => {
    if (!threadId || !near) return;
    let cancelled = false;
    api.get<{ url: string }>(`/chat/threads/${threadId}/workspace/download`, { path })
      .then((r) => { if (!cancelled) setUrl(r.url); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [threadId, path, near, attempt]);

  const onError = useCallback(() => {
    if (attempt === 0) setAttempt(1);
    else setFailed(true);
  }, [attempt]);

  return { ref, url, failed, onError };
}
